import React, { useEffect, useMemo, useState } from 'react';
import { Table, Button, Space, message, Typography, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { quotationService, Quotation } from '../services/quotations';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { ResizableTableHeader } from '../components/ResizableTableHeader';
import { ColumnVisibilityToggle } from '../components/ColumnVisibilityToggle';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import '../styles/resizable-table.css';

const { Title } = Typography;

type FilterType = 'all' | 'draft' | 'Pending' | 'Approved';

export const Quotations: React.FC = () => {
  const [quotations, setQuotations] = useState<Quotation[]>([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState<FilterType>('all');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canView = hasPermission(user, 'quotations.view');
  const canCreate = hasPermission(user, 'quotations.create');

  useEffect(() => {
    if (!canView) {
      setQuotations([]);
      return;
    }
    fetchData();
  }, [canView]);

  const fetchData = async () => {
    if (!canView) {
      return;
    }
    setLoading(true);
    try {
      const response = await quotationService.getAll();
      setQuotations(response.data || []);
    } catch {
      message.error('Failed to load quotations data');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await quotationService.delete(id);
      message.success('Quotation deleted');
      fetchData();
    } catch {
      message.error('Failed to delete quotation');
    }
  };

  const baseColumns = [
    { 
      title: 'Quotation Number', 
      dataIndex: 'quotation_number', 
      key: 'quotation_number', 
      width: 180,
      sorter: (a: Quotation, b: Quotation) => (a.quotation_number || '').localeCompare(b.quotation_number || ''),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      sorter: (a: Quotation, b: Quotation) => {
        if (!a.date && !b.date) return 0;
        if (!a.date) return 1;
        if (!b.date) return -1;
        return new Date(a.date).getTime() - new Date(b.date).getTime();
      },
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      width: 180,
      sorter: (a: Quotation, b: Quotation) => (a.customer?.name || '').localeCompare(b.customer?.name || ''),
      render: (customer: any) => customer?.name || '-',
    },
    {
      title: 'Agent',
      dataIndex: 'agent',
      key: 'agent',
      width: 160,
      sorter: (a: Quotation, b: Quotation) => (a.agent?.name || '').localeCompare(b.agent?.name || ''),
      render: (agent: any) => agent?.name || '-',
    },
    {
      title: 'Team',
      dataIndex: 'team',
      key: 'team',
      width: 160,
      sorter: (a: Quotation, b: Quotation) => (a.team?.name || '').localeCompare(b.team?.name || ''),
      render: (team: any) => team?.name || '-',
    },
    {
      title: 'Staff',
      dataIndex: 'staff',
      key: 'staff',
      width: 160,
      sorter: (a: Quotation, b: Quotation) => (a.staff?.name || '').localeCompare(b.staff?.name || ''),
      render: (staff: any) => staff?.name || '-',
    },
    {
      title: 'Invoice #',
      dataIndex: 'invoice',
      key: 'invoice',
      width: 150,
      sorter: (a: Quotation, b: Quotation) => (a.invoice?.invoice_number || '').localeCompare(b.invoice?.invoice_number || ''),
      render: (invoice: any) => invoice?.invoice_number || '-',
    },
  ];

  const {
    columnConfig,
    visibleColumns,
    visibleKeys,
    handleColumnVisibilityChange,
  } = useColumnVisibility(baseColumns);

  const { columns: resizableColumns } = useResizableColumns(visibleColumns);

  const memoizedColumns = useMemo(
    () =>
      resizableColumns.map((col) => ({
        ...col,
        width: col.width, // Preserve width
        onHeaderCell: col.onHeaderCell, // Preserve onHeaderCell for resizing
        getSearchValue:
          col.dataIndex === 'quotation_number'
            ? (record: Quotation) => record.quotation_number
            : col.dataIndex === 'date'
            ? (record: Quotation) => record.date
            : col.dataIndex === 'customer'
            ? (record: Quotation) => record.customer?.name
            : col.dataIndex === 'agent'
            ? (record: Quotation) => record.agent?.name
            : col.dataIndex === 'team'
            ? (record: Quotation) => record.team?.name
            : col.dataIndex === 'staff'
            ? (record: Quotation) => record.staff?.name
            : col.dataIndex === 'invoice'
            ? (record: Quotation) => record.invoice?.invoice_number
            : (undefined as unknown as (record: Quotation) => string | number | null | undefined),
      })),
    [resizableColumns]
  );

  const filteredQuotations = useMemo(() => {
    if (filter === 'all') {
      return quotations;
    } else if (filter === 'draft') {
      return quotations.filter(quotation => quotation.status === 'draft' || !quotation.status);
    } else if (filter === 'Pending') {
      return quotations.filter(quotation => quotation.status === 'Pending');
    } else if (filter === 'Approved') {
      return quotations.filter(quotation => quotation.status === 'Approved');
    }
    return quotations;
  }, [quotations, filter]);

  const { columnsWithSearch, filteredData } = useTableColumnSearch<Quotation>(filteredQuotations, memoizedColumns as any);

  return (
    <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0 }}>
            Quotations
          </Title>
          <Space>
            <ColumnVisibilityToggle
              columns={columnConfig}
              visibleKeys={visibleKeys}
              onChange={handleColumnVisibilityChange}
            />
          {canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/quotations/new')}>
              New Quotation
            </Button>
          )}
        </Space>
      </div>

        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button 
              type={filter === 'all' ? 'primary' : 'default'}
              onClick={() => setFilter('all')}
            >
              All
            </Button>
            <Button 
              type={filter === 'draft' ? 'primary' : 'default'}
              onClick={() => setFilter('draft')}
            >
              Draft
            </Button>
            <Button 
              type={filter === 'Pending' ? 'primary' : 'default'}
              onClick={() => setFilter('Pending')}
            >
              Pending
            </Button>
            <Button 
              type={filter === 'Approved' ? 'primary' : 'default'}
              onClick={() => setFilter('Approved')}
            >
              Approved
          </Button>
          </Space>
        </div>

        <Table
          columns={columnsWithSearch}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ y: 'calc(100vh - 400px)' }}
          components={{
            header: {
              cell: ResizableTableHeader,
            },
          }}
          rowSelection={{
            selectedRowKeys,
            onChange: (keys) => setSelectedRowKeys(keys),
            hideSelectAll: true,
            columnWidth: 0,
            renderCell: () => null,
          }}
          footer={() => (
            <div style={{ textAlign: 'left', padding: '8px 0' }}>
              Total: {filteredData.length} {filteredData.length === 1 ? 'quotation' : 'quotations'}
            </div>
          )}
          onRow={(record) => ({
            onClick: () => setSelectedRowKeys([record.id]),
            onDoubleClick: canView ? () => navigate(`/quotations/${record.id}/edit`) : undefined,
            style: { cursor: canView ? 'pointer' : 'default' },
          })}
        />
    </div>
  );
};
